import axios from "axios";

const BASE_URL = (
  process.env.REACT_APP_BASE_URL ||
  "https://services.allhomedesire.com"
).replace(/\/+$/, "");

export const getAuthHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/* =========================================================================
   1. PARTNER LEADS APIS (Grouped & Detailed Leads)
   ========================================================================= */

// Grouped Partner Leads (matches mobile Leads.tsx)
export const getGroupedPartnerLeads = async (
  token,
  { page_number = 1, page_size = 20, order_by = "DESC", keyword_search = "" } = {}
) => {
  const response = await axios.post(
    `${BASE_URL}/partnerLead/grouped-leads`,
    { page_number, page_size, order_by, keyword_search },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Raw Partner Leads
export const getPartnerLeads = async (
  token,
  { page_number = 1, page_size = 20, order_by = "DESC", keyword_search = "" } = {}
) => {
  const response = await axios.post(
    `${BASE_URL}/partnerLead/leads`,
    { page_number, page_size, order_by, keyword_search },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

// Customer location & details by ID for lead cards
export const getCustomerDetailsById = async (token, customerId) => {
  try {
    const response = await axios.get(`${BASE_URL}/admin/user/id/${customerId}`, {
      headers: getAuthHeaders(token),
    });
    const user = response.data?.user || response.data || {};
    return {
      id: customerId,
      name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.name || "Customer",
      city: user.city || user.area_name || "Prime Location",
      state: user.state || "",
      phone: user.mobile_number || user.phone || "",
      photo: user.photo ? `https://images.allhomedesire.com${user.photo}` : null,
      email: user.email_address || user.email || "",
    };
  } catch (err) {
    return {
      id: customerId,
      name: "Customer",
      city: "Prime Location",
      state: "",
      phone: "",
      photo: null,
      email: "",
    };
  }
};

// WhatsApp Bulk Template Message Dispatch
export const sendPartnerLeadWhatsApp = async (
  token,
  { customerPhone, customerName, leadName, area, city, state, senderName, senderPhone }
) => {
  const formattedPhone = customerPhone.startsWith("+") ? customerPhone : `+91${customerPhone}`;
  const response = await axios.post(
    `${BASE_URL}/all/whatsapp/bulk-template`,
    {
      templateName: "property_recommendation",
      namespace: "05bfd9ab_ad0a_450f_a722_2860545f1427",
      to: [formattedPhone],
      bodyValues: {
        body_1: customerName || "Customer",
        body_2: leadName || "Property Inquiry",
        body_3: "-",
        body_4: area || "",
        body_5: city || "",
        body_6: state || "",
        body_7: senderName || "Verified Partner",
        body_8: senderPhone || "",
      },
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   2. INTERIOR DESIGN CATALOGS APIS (Interior Partner service_id === 1)
   ========================================================================= */

export const getInteriorCatalogTypes = async (token) => {
  const response = await axios.post(
    `${BASE_URL}/interior/catalog/types`,
    {},
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getInteriorCatalogs = async (
  token,
  { page_number = 1, page_size = 20, order_by = "DESC", keyword_search = "", type_id = "", min_price = 0, max_price = "" } = {}
) => {
  const response = await axios.post(
    `${BASE_URL}/interior/catalogs`,
    {
      page_number,
      page_size,
      order_by,
      keyword_search,
      type_id,
      min_price: Number(min_price) || 0,
      max_price: max_price ? Number(max_price) : "",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getMySpaceInteriorCatalogs = async (token, { page_number = 1, page_size = 50 } = {}) => {
  const response = await axios.post(
    `${BASE_URL}/interior/catalogs/mySpace`,
    { page_number, page_size, order_by: "DESC" },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const addInteriorCatalog = async (token, catalogData) => {
  const response = await axios.post(
    `${BASE_URL}/interior/catalog/add`,
    catalogData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const editInteriorCatalog = async (token, catalogData) => {
  const response = await axios.put(
    `${BASE_URL}/interior/catalog/edit`,
    catalogData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deleteInteriorCatalog = async (token, catalogId) => {
  const response = await axios.delete(
    `${BASE_URL}/interior/catalog/delete/${catalogId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getInteriorCatalogDetails = async (token, catalogId) => {
  const response = await axios.get(
    `${BASE_URL}/interior/catalog/details/${catalogId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   3. LOCAL BAZAAR MERCHANT APIS (Local Bazaar Partner service_id === 4)
   ========================================================================= */

export const getPartnerLocalBazaarCategories = async (token) => {
  const response = await axios.post(
    `${BASE_URL}/api/local/bazaar/categories/list`,
    { page_number: 1, page_size: 50, order_by: "DESC" },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getPartnerLocalBazaars = async (
  token,
  { page_number = 1, page_size = 20, partnerId = null, keyword_search = "", category_id = "" } = {}
) => {
  const payload = {
    page_number,
    page_size,
    order_by: "DESC",
  };
  if (partnerId) payload.user_id = partnerId;
  if (keyword_search) payload.keyword_search = keyword_search;
  if (category_id) payload.category_id = Number(category_id);

  const response = await axios.post(
    `${BASE_URL}/api/local/bazaar/`,
    payload,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const addPartnerLocalBazaar = async (token, itemData) => {
  const response = await axios.post(
    `${BASE_URL}/api/local/bazaar/add`,
    itemData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const editPartnerLocalBazaar = async (token, itemData) => {
  const response = await axios.put(
    `${BASE_URL}/api/local/bazaar/edit`,
    itemData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deletePartnerLocalBazaar = async (token, bazaarId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/local/bazaar/delete/${bazaarId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getPartnerLocalBazaarDetails = async (token, bazaarId) => {
  const response = await axios.get(
    `${BASE_URL}/api/local/bazaar/details/${bazaarId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   4. ALL SERVICES PROVIDER APIS (Services Partner service_id === 3)
   ========================================================================= */

export const getPartnerServiceTypes = async (token) => {
  const response = await axios.post(
    `${BASE_URL}/api/all/services/types`,
    { page_number: 1, page_size: 50, order_by: "DESC" },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getPartnerServicesMySpace = async (token, partnerId) => {
  const response = await axios.post(
    `${BASE_URL}/api/all/services/mySpace`,
    {
      user_id: partnerId,
      keywordSearch: "",
      typeId: "",
      page_number: 1,
      page_size: 50,
      order_by: "DESC",
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const addPartnerService = async (token, serviceData) => {
  const response = await axios.post(
    `${BASE_URL}/api/all/services/add`,
    serviceData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const editPartnerService = async (token, serviceData) => {
  const response = await axios.put(
    `${BASE_URL}/api/all/services/edit`,
    serviceData,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const deletePartnerService = async (token, serviceId) => {
  const response = await axios.delete(
    `${BASE_URL}/api/all/services/delete/${serviceId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getPartnerServiceDetails = async (token, serviceId) => {
  const response = await axios.get(
    `${BASE_URL}/api/all/services/details/${serviceId}`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   5. PARTNER SUBSCRIPTIONS & CURRENT PLAN APIS
   ========================================================================= */

export const getPartnerSubscriptionDetails = async (token) => {
  const response = await axios.get(
    `${BASE_URL}/api/payments/subscription/details`,
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

export const getPartnerPaidSubscriptionPlans = async (token, serviceId = 2) => {
  const response = await axios.post(
    `${BASE_URL}/api/subscription/plan/list/paid/by/service`,
    {
      order_by: "ASC",
      all_service_id: Number(serviceId),
      pageNumber: 1,
      pageSize: 50,
    },
    { headers: getAuthHeaders(token) }
  );
  return response.data;
};

/* =========================================================================
   6. MULTI-MEDIA FILE UPLOAD UTILITY
   ========================================================================= */

export const uploadMediaFiles = async (token, files) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("files", file);
  });

  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const response = await axios.post(
    `${BASE_URL}/all/upload/files/images`,
    formData,
    { headers }
  );
  return response.data;
};
